import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  IconButton,
  Card,
  CardContent,
  Alert,
  Divider,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';

// SAP Design System
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { SAPIconButton, SAPComboBox, PageLayout, FilterBar } from '../components';
import { logger } from '../utils/logger';

import { fetchMails, fetchMailsCount, fetchMailEnums } from '../api/features/mail/mailApi';
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchJobDetails, fetchJobNames } from '../api/features/jobDetail/jobDetailApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import type { MailData } from '../api/features/mail/mail.types';
import type { Client } from '../api/features/clientData/client.types';

const ROWS_PER_PAGE = 10;

// SAP Styled Components

const SAPFilterCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
  marginBottom: SAP_SPACING.xl,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.sm,
}));

const SAPTableHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  color: SAP_COLORS.sapGray7,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
  borderBottom: `2px solid ${SAP_COLORS.sapGray3}`,
  fontSize: '0.875rem',
}));

const SAPTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: '4px',
    '& fieldset': {
      borderColor: SAP_COLORS.sapGray3,
    },
    '&:hover fieldset': {
      borderColor: SAP_COLORS.sapBlue,
    },
    '&.Mui-focused fieldset': {
      borderColor: SAP_COLORS.sapBlue,
      boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightBlue}`,
    },
  },
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
  '& .MuiInputBase-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
}));

const Mail: React.FC = () => {
  const { clientCode } = useClientCode();
  const isAdmin = clientCode === 'admin';
  const [clients, setClients] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [mailTypes, setMailTypes] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [selectedMailType, setSelectedMailType] = useState<any>(null);
  const [mailData, setMailData] = useState<MailData[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Load client options on component mount (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
      .then((clientsData) => {
        setClients(clientsData.map((c: Client) => ({ label: c.name, value: c.id, code: c.code })));
      })
      .catch(() => setClients([]));
  }, [clientCode, isAdmin]);

  // Load mail type enums on component mount
  useEffect(() => {
    fetchMailEnums({ clientCode })
      .then((enums) => {
        // Handle different response formats
        let mailTypeOptions: any[] = [];
        
        if (Array.isArray(enums)) {
          // If response is an array of strings
          mailTypeOptions = enums.map((enumValue: string) => ({
            label: enumValue,
            value: enumValue,
          }));
        } else if (typeof enums === 'object' && enums !== null) {
          // If response is an object, extract values
          const enumValues = Object.values(enums);
          mailTypeOptions = enumValues.map((enumValue: any) => ({
            label: String(enumValue),
            value: String(enumValue),
          }));
        }
        
        setMailTypes(mailTypeOptions);
      })
      .catch((err) => {
        logger.error('Error fetching mail enums:', err);
        setMailTypes([]);
      });
  }, [clientCode]);

  // Load job options
  // - Admin: when a client is selected
  // - Client users: all jobs for their clientCode (no explicit client selector)
  useEffect(() => {
    if (isAdmin) {
      if (selectedClient && selectedClient.value) {
        fetchJobDetails({ clientCode, clientId: selectedClient.value, firstResult: -1, maxResult: 0 })
          .then((jobs) =>
            setJobs(
              jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))
            )
          )
          .catch(() => setJobs([]));
        setSelectedJob(null);
      } else {
        setJobs([]);
        setSelectedJob(null);
      }
      return;
    }

    // Client users: fetch jobs scoped by their clientCode
    fetchJobDetails({ clientCode, firstResult: -1, maxResult: 0 })
      .then((jobs) =>
        setJobs(
          jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))
        )
      )
      .catch(() => setJobs([]));
  }, [selectedClient, clientCode, isAdmin]);

  // Fetch mail data and count
  const fetchData = async (pageOverride?: number) => {
    setLoading(true);
    setError(null);
    try {
      const currentPage = pageOverride ?? page;
      const firstResult = currentPage * ROWS_PER_PAGE;
      
      const params: any = {
        clientCode,
        firstResult,
        maxResult: ROWS_PER_PAGE,
        searched: true,
      };
      
      // Only add parameters if they are selected
      if (selectedClient?.value) params.clientId = selectedClient.value;
      if (selectedJob?.value) params.jobDetailId = selectedJob.value;
      if (selectedMailType?.value) params.mailType = selectedMailType.value;
      
      const [mailDataResponse, mailCount] = await Promise.all([
        fetchMails(params),
        fetchMailsCount({
          clientCode,
          clientId: selectedClient?.value,
          jobDetailId: selectedJob?.value,
          mailType: selectedMailType?.value,
          searched: true,
        }),
      ]);
      
      logger.log('Mail data response:', mailDataResponse);
      logger.log('Mail count response:', mailCount);
      logger.log('Params sent:', params);
      
      setMailData(mailDataResponse);
      setRowCount(mailCount);
    } catch (err: any) {
      setError('Failed to load mail data');
      setMailData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
    fetchData(0);
  };

  const handleReset = () => {
    setSelectedClient(null);
    setSelectedJob(null);
    setSelectedMailType(null);
    setPage(0);
    setHasSearched(false);
    setError(null);
    setMailData([]);
    setRowCount(0);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleClientChange = (value: any) => {
    setSelectedClient(value);
  };

  const handleJobChange = (value: any) => {
    setSelectedJob(value);
  };

  const formatDate = (dateValue: string | number) => {
    if (!dateValue) return '';
    
    let date: Date;
    
    // Handle different date formats
    if (typeof dateValue === 'number') {
      // If it's a timestamp (number)
      date = new Date(dateValue);
    } else if (typeof dateValue === 'string') {
      // If it's a string, try to parse it
      const parsedDate = new Date(dateValue);
      // Check if the parsed date is valid
      if (isNaN(parsedDate.getTime())) {
        // Try parsing as timestamp if string parsing failed
        const timestamp = parseInt(dateValue);
        if (!isNaN(timestamp)) {
          date = new Date(timestamp);
        } else {
          return 'Invalid Date';
        }
      } else {
        date = parsedDate;
      }
    } else {
      return 'Invalid Date';
    }
    
    // Check if the final date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    // Format the date as YYYY-MM-DD HH:MM
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const autocompleteSx = {
    '& .MuiAutocomplete-paper': {
      bgcolor: '#fff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '& .MuiAutocomplete-listbox': {
      color: '#222 !important',
      '& .MuiAutocomplete-option': {
        color: '#222 !important',
        fontWeight: 600,
        '&:hover': {
          bgcolor: '#f5f5f5',
        },
        '&.Mui-focused': {
          bgcolor: '#e3f2fd',
        },
      },
    },
    '& .MuiAutocomplete-option': {
      color: '#222 !important',
      fontWeight: 600,
      '&:hover': {
        bgcolor: '#f5f5f5',
      },
      '&.Mui-focused': {
        bgcolor: '#e3f2fd',
      },
    },
    '& .MuiAutocomplete-input': {
      color: '#222 !important',
      fontWeight: 600,
    },
    '& .MuiAutocomplete-endAdornment': {
      color: '#222 !important',
    },
    '& .MuiAutocomplete-clearIndicator': {
      color: '#222 !important',
    },
    '& .MuiAutocomplete-popupIndicator': {
      color: '#222 !important',
    },
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      height: 40,
      bgcolor: '#fff',
      '& fieldset': {
        borderColor: '#ddd',
      },
      '&:hover fieldset': {
        borderColor: '#999',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1976d2',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#666',
      fontSize: '0.875rem',
    },
    '& .MuiInputBase-input': {
      color: '#222 !important',
      fontWeight: 600,
    },
  };

  return (
    <PageLayout
      title="Mail"
      description="Search and view mail data for your clients"
      titleSx={{ color: SAP_COLORS.sapBlue }}
    >
      <FilterBar loading={loading}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          gap: SAP_SPACING.md, 
          alignItems: { xs: 'stretch', md: 'center' },
          width: '100%',
        }}>
          {/* Client Dropdown (admin only) */}
          {isAdmin && (
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 250 } }}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => option.label}
                value={selectedClient}
                onChange={(_, v) => handleClientChange(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Client Name" />
                )}
              />
            </Box>
          )}
          
          {/* Job Name Dropdown */}
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 250 } }}>
            <Autocomplete
              options={jobs}
              getOptionLabel={(option) => option.label}
              value={selectedJob}
              onChange={(_, v) => handleJobChange(v)}
              disabled={isAdmin && !selectedClient}
              renderInput={(params) => (
                <TextField {...params} label="Job Name" />
              )}
            />
          </Box>

          {/* Mail Type Dropdown */}
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 250 } }}>
            <Autocomplete
              options={mailTypes}
              getOptionLabel={(option) => option.label}
              value={selectedMailType}
              onChange={(_, v) => setSelectedMailType(v)}
              renderInput={(params) => (
                <TextField {...params} label="Mail Type" />
              )}
            />
          </Box>
          
          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: SAP_SPACING.sm, 
            minWidth: { xs: '100%', md: 140 }, 
            justifyContent: { xs: 'center', md: 'flex-start' } 
          }}>
            <SAPIconButton
              icon="search"
              onClick={handleSearch}
              variant="primary"
              loading={loading}
            />
            <SAPIconButton
              icon="reset"
              onClick={handleReset}
              variant="secondary"
            />
          </Box>
        </Box>
      </FilterBar>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            borderRadius: '8px',
          }}
        >
          {error}
        </Alert>
      )}

      {/* Data Display */}
      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ padding: 0 }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              padding: SAP_SPACING.xl 
            }}>
              <CircularProgress 
                size={40} 
                sx={{ color: SAP_COLORS.sapBlue }} 
              />
            </Box>
          ) : !hasSearched && mailData.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              padding: SAP_SPACING.xl 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                  marginBottom: SAP_SPACING.md,
                }}
              >
                Please use the search filters above to view mail data
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: SAP_COLORS.sapGray4,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }}
              >
                Select client, job, or mail type to filter results
              </Typography>
            </Box>
          ) : (
            <>
              {/* SAP Table */}
              <TableContainer 
                component={Paper} 
                sx={{ 
                  boxShadow: 'none', 
                  backgroundColor: SAP_COLORS.sapWhite 
                }}
              >
                <Table sx={{ minWidth: 900 }} stickyHeader aria-label="mail table">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: SAP_COLORS.sapLightBlue }}>
                      <SAPTableHeader>S.No.</SAPTableHeader>
                      <SAPTableHeader>Date</SAPTableHeader>
                      <SAPTableHeader>From Mail</SAPTableHeader>
                      <SAPTableHeader>To Mail</SAPTableHeader>
                      <SAPTableHeader>Subject</SAPTableHeader>
                      <SAPTableHeader>Mail Type</SAPTableHeader>
                      <SAPTableHeader>Header</SAPTableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ backgroundColor: SAP_COLORS.sapWhite }}>
                    {mailData.length === 0 ? (
                      <TableRow>
                        <TableCell 
                          colSpan={7} 
                          align="center" 
                          sx={{ 
                            color: SAP_COLORS.sapGray4,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            padding: SAP_SPACING.xl,
                          }}
                        >
                          No mail data found
                        </TableCell>
                      </TableRow>
                    ) : (
                      mailData.map((row, idx) => (
                        <TableRow
                          key={`mail-${page * ROWS_PER_PAGE + idx + 1}-${row.lastUpdateDate || idx}-${row.fromMail || idx}-${row.toMail || idx}`}
                          sx={{
                            backgroundColor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            '&:hover': { 
                              backgroundColor: SAP_COLORS.hover,
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {page * ROWS_PER_PAGE + idx + 1}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {formatDate(row.lastUpdateDate)}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.fromMail}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.toMail}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.subject}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapBlue,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.mailType}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          }}>
                            {row.header}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* SAP Pagination */}
              {/* Pagination */}
              {mailData.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + mailData.length, rowCount)} of {rowCount} mails
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(rowCount / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handleChangePage(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
};

export default Mail;